import RedeemIcon from "@mui/icons-material/Redeem";
import {
  Badge,
  Button,
  CircularProgress,
  ClickAwayListener,
  Fade,
  IconButton,
  Paper,
  Popper,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useVoucher from "../../hooks/useVoucher";
import { Voucher } from "../../utils/api/voucher";
import { createClickEngagementRecorder } from "../../utils/api";
import { useWhiteboardRoom } from "../../hooks/useWhiteboardRoom";
import "./index.css";

const InvitePopper: React.FC = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [clickedIndex, setClickedIndex] = useState<null | number>(null);
  const { voucherList, isLoading: isFetchingVoucher } = useVoucher();
  const theme = useTheme();
  const validVoucherList = useMemo(() => {
    return voucherList.filter((voucher: Voucher) => voucher.is_valid);
  }, [voucherList]);

  const { roomDomain } = useWhiteboardRoom();
  const clickRecorder = createClickEngagementRecorder(roomDomain, roomDomain);

  const handleInviteBtnClick = (e: React.MouseEvent<HTMLElement>) => {
    clickRecorder("menu-inviter");
    setAnchorEl(e.currentTarget);
    setIsOpen((prev) => !prev);
  };

  const copyToClipboard = (voucher: string) => {
    const copiedText = document.createElement("input");
    document.body.appendChild(copiedText);
    copiedText.setAttribute("value", voucher);
    copiedText.select();
    document.execCommand("copy");
    document.body.removeChild(copiedText);
  };

  const clickCopy = (voucher: string, index: number) => {
    copyToClipboard(voucher);
    setClickedIndex(index);
    setTimeout(() => {
      setClickedIndex(null);
    }, 3000);
  };

  const getTooltipTitle = (voucher: Voucher, index: number): string => {
    if (!voucher.is_valid) return t("Invite_Popper_Voucher_Used");
    if (index === clickedIndex) return t("Invite_Popper_Voucher_Copied");
    return t("Invite_Popper_Voucher_Copy");
  };

  return (
    <ClickAwayListener onClickAway={() => setIsOpen(false)}>
      <div>
        <Tooltip
          title={t("Menu_Activation_Codes")}
          placement="left-end"
          arrow
          disableInteractive
        >
          <IconButton aria-label="invite" onClick={handleInviteBtnClick}>
            <Badge
              badgeContent={validVoucherList.length}
              color="primary"
              max={9}
            >
              <RedeemIcon color="action" fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>
        <Popper
          open={isOpen}
          anchorEl={anchorEl}
          placement="left-start"
          transition
          modifiers={[{ name: "arrow", enabled: true }]}
          style={{ zIndex: 999 }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <Paper className="InvitePopper-paper">
                <h3>{t("Invite_Popper_Title")}</h3>
                <div>
                  {validVoucherList.length + " " + t("Invite_Popper_Code_2")}
                </div>
                <div className="InvitePopper-voucher-outer">
                  {isFetchingVoucher && !voucherList.length && (
                    <CircularProgress
                      style={{ color: theme.brandColor, padding: "10px 0" }}
                      size={15}
                    />
                  )}
                  {voucherList.map((v: Voucher, i: number) => (
                    <div key={`voucherBtn_${i}`}>
                      <Tooltip
                        title={getTooltipTitle(v, i)}
                        arrow
                        disableInteractive
                      >
                        <span>
                          <Button
                            size="small"
                            className="InvitePopper-voucher"
                            onClick={() =>
                              clickCopy(`${v.token}@${v.invited_by}`, i)
                            }
                            type="button"
                            disabled={!v.is_valid}
                          >
                            {`${v.token}@${v.invited_by}`}
                          </Button>
                        </span>
                      </Tooltip>
                    </div>
                  ))}
                  {isFetchingVoucher && !!voucherList.length && (
                    <div className="InvitePopper-voucher-loader">
                      <CircularProgress
                        style={{
                          color: theme.brandColor,
                          margin: "2px 8px 0 0",
                        }}
                        size={12}
                      />{" "}
                      {t("Invite_Popper_Voucher_Loader")}
                    </div>
                  )}
                </div>
                <div>
                  <span className="InvitePopper-voucher-bottom">
                    {t("Invite_Popper_Promote")}
                  </span>
                </div>
              </Paper>
            </Fade>
          )}
        </Popper>
      </div>
    </ClickAwayListener>
  );
};

export default InvitePopper;
